export interface ChunkErrorSessionState {
  errorCount: number;
  isReloadInProgress: boolean;
  isPostReload: boolean;
}

// In-memory fallback for environments where sessionStorage throws — notably Safari Private
// Mode (QuotaExceededError on setItem). We only switch to the memory store AFTER sessionStorage
// actually fails, so normal sessions behave exactly like direct sessionStorage access (no
// shadow copy that could go stale). Without this the whole chunk-recovery path would crash on
// the very browsers that hit chunk errors most.
const memoryStore = new Map<string, string>();
let useMemoryFallback = false;

function safeGet(key: string): string | null {
  if (!useMemoryFallback) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      useMemoryFallback = true;
    }
  }
  return memoryStore.get(key) ?? null;
}

function safeSet(key: string, value: string): void {
  if (!useMemoryFallback) {
    try {
      sessionStorage.setItem(key, value);
      return;
    } catch {
      useMemoryFallback = true;
    }
  }
  memoryStore.set(key, value);
}

function safeRemove(key: string): void {
  if (!useMemoryFallback) {
    try {
      sessionStorage.removeItem(key);
      return;
    } catch {
      useMemoryFallback = true;
    }
  }
  memoryStore.delete(key);
}

/** Test-only: reset the in-memory storage fallback so cases don't leak state into each other. */
export function __resetStorageFallbackForTests(): void {
  useMemoryFallback = false;
  memoryStore.clear();
}

export class ChunkErrorSessionManager {
  private static readonly CHUNK_ERROR_COUNT_KEY = 'chunk_error_count';
  private static readonly POST_RELOAD_FLAG_KEY = 'chunk_post_reload';
  private static readonly RELOAD_IN_PROGRESS_KEY = 'chunk_reload_in_progress';

  static getState(): ChunkErrorSessionState {
    return {
      errorCount: this.getErrorCount(),
      isReloadInProgress: this.isReloadInProgress(),
      isPostReload: this.isPostReload(),
    };
  }

  private static getErrorCount(): number {
    const count = safeGet(this.CHUNK_ERROR_COUNT_KEY);
    const parsed = count ? Number.parseInt(count, 10) : 0;
    // Guard against a corrupted non-numeric value (NaN would otherwise be written back as 'NaN').
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  static incrementErrorCount(): number {
    const newCount = this.getErrorCount() + 1;
    safeSet(this.CHUNK_ERROR_COUNT_KEY, newCount.toString());
    return newCount;
  }

  static clearErrorCount(): void {
    safeRemove(this.CHUNK_ERROR_COUNT_KEY);
    safeRemove(this.RELOAD_IN_PROGRESS_KEY);
  }

  static clearAllFlags(): void {
    safeRemove(this.CHUNK_ERROR_COUNT_KEY);
    safeRemove(this.POST_RELOAD_FLAG_KEY);
    safeRemove(this.RELOAD_IN_PROGRESS_KEY);
  }

  private static isReloadInProgress(): boolean {
    return safeGet(this.RELOAD_IN_PROGRESS_KEY) === 'true';
  }

  static markReloadInProgress(): void {
    safeSet(this.RELOAD_IN_PROGRESS_KEY, 'true');
  }

  private static isPostReload(): boolean {
    return safeGet(this.POST_RELOAD_FLAG_KEY) === 'true';
  }

  static markAsPostReload(): void {
    safeSet(this.POST_RELOAD_FLAG_KEY, 'true');
  }

  static cleanupStaleReloadFlag(): void {
    if (this.isReloadInProgress()) {
      safeRemove(this.RELOAD_IN_PROGRESS_KEY);
    }
  }
}
